import { useState, useCallback, useRef, useEffect } from 'react'
import { BubbleMenu } from '@tiptap/react/menus'
import type { Editor } from '@tiptap/react'
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Highlighter,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ChevronDown,
} from 'lucide-react'

interface SelectionToolbarProps {
  editor: Editor
}

export function SelectionToolbar({ editor }: SelectionToolbarProps) {
  const [showBlockMenu, setShowBlockMenu] = useState(false)
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down')
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  // Determine dropdown direction based on available viewport space
  useEffect(() => {
    if (!showBlockMenu || !dropdownTriggerRef.current) return
    const rect = dropdownTriggerRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const dropdownHeight = 220 // approximate height of the menu
    setDropdownDirection(spaceBelow < dropdownHeight ? 'up' : 'down') // eslint-disable-line react-hooks/set-state-in-effect -- DOM measurement
  }, [showBlockMenu])

  const toggleBlockMenu = useCallback(() => {
    setShowBlockMenu((prev) => !prev)
  }, [])

  const closeBlockMenu = useCallback(() => {
    setShowBlockMenu(false)
  }, [])

  // Get the current active block label for the dropdown
  const getActiveBlockLabel = () => {
    if (editor.isActive('heading', { level: 1 })) return 'H1'
    if (editor.isActive('heading', { level: 2 })) return 'H2'
    if (editor.isActive('heading', { level: 3 })) return 'H3'
    if (editor.isActive('bulletList')) return 'Bullet'
    if (editor.isActive('orderedList')) return 'Number'
    return 'Text'
  }

  const blockItems = [
    {
      id: 'paragraph',
      label: 'Text',
      icon: null,
      action: () => editor.chain().focus().setParagraph().run(),
      isActive: () =>
        !editor.isActive('heading') &&
        !editor.isActive('bulletList') &&
        !editor.isActive('orderedList'),
    },
    {
      id: 'heading1',
      label: 'Heading 1',
      icon: Heading1,
      action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: () => editor.isActive('heading', { level: 1 }),
    },
    {
      id: 'heading2',
      label: 'Heading 2',
      icon: Heading2,
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: () => editor.isActive('heading', { level: 2 }),
    },
    {
      id: 'heading3',
      label: 'Heading 3',
      icon: Heading3,
      action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: () => editor.isActive('heading', { level: 3 }),
    },
    {
      id: 'bulletList',
      label: 'Bullet List',
      icon: List,
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: () => editor.isActive('bulletList'),
    },
    {
      id: 'orderedList',
      label: 'Numbered List',
      icon: ListOrdered,
      action: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: () => editor.isActive('orderedList'),
    },
  ]

  const inlineFormats = [
    {
      id: 'bold',
      icon: Bold,
      label: 'Bold',
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: () => editor.isActive('bold'),
    },
    {
      id: 'italic',
      icon: Italic,
      label: 'Italic',
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: () => editor.isActive('italic'),
    },
    {
      id: 'underline',
      icon: Underline,
      label: 'Underline',
      action: () => editor.chain().focus().toggleUnderline().run(),
      isActive: () => editor.isActive('underline'),
    },
    {
      id: 'strike',
      icon: Strikethrough,
      label: 'Strikethrough',
      action: () => editor.chain().focus().toggleStrike().run(),
      isActive: () => editor.isActive('strike'),
    },
    {
      id: 'highlight',
      icon: Highlighter,
      label: 'Highlight',
      action: () => editor.chain().focus().toggleHighlight().run(),
      isActive: () => editor.isActive('highlight'),
    },
    {
      id: 'code',
      icon: Code,
      label: 'Inline Code',
      action: () => editor.chain().focus().toggleCode().run(),
      isActive: () => editor.isActive('code'),
    },
  ]

  // Detect mobile (< 640px)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Track selection changes so the mobile toolbar reacts to select/deselect
  const [hasSelection, setHasSelection] = useState(false)
  useEffect(() => {
    const onSelectionUpdate = () => {
      const { from, to } = editor.state.selection
      setHasSelection(from !== to)
    }
    editor.on('selectionUpdate', onSelectionUpdate)
    return () => { editor.off('selectionUpdate', onSelectionUpdate) }
  }, [editor])

  // On mobile, render a fixed sidebar instead of BubbleMenu
  if (isMobile) {
    if (!hasSelection) return null

    return (
      <div
        className="fixed right-1 top-1/2 -translate-y-1/2 z-50 flex flex-col rounded-lg border shadow-lg"
        style={{
          backgroundColor: 'hsl(var(--color-bg-secondary))',
          borderColor: 'hsl(var(--color-border))',
        }}
      >
        {/* Block type dropdown */}
        <div className="relative">
          <button
            ref={dropdownTriggerRef}
            onClick={toggleBlockMenu}
            className="flex items-center justify-center gap-0.5 px-1.5 py-1 text-[10px] font-medium transition-fast hover:bg-black/5 dark:hover:bg-white/10 rounded-t-lg border-b"
            style={{
              color: 'hsl(var(--color-text-secondary))',
              borderColor: 'hsl(var(--color-border))',
            }}
            title="Change block type"
          >
            <span>{getActiveBlockLabel()}</span>
            <ChevronDown className="w-2.5 h-2.5" />
          </button>

          {showBlockMenu && (
            <div
              className="absolute right-full mr-1 top-0 w-40 rounded-lg border shadow-xl overflow-hidden z-50"
              style={{
                backgroundColor: 'hsl(var(--color-bg-secondary))',
                borderColor: 'hsl(var(--color-border))',
              }}
            >
              <div className="py-0.5">
                {blockItems.map((item) => {
                  const Icon = item.icon
                  const active = item.isActive()
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        item.action()
                        closeBlockMenu()
                      }}
                      className="w-full flex items-center gap-2 px-2.5 py-1 text-xs text-left transition-fast hover:bg-black/5 dark:hover:bg-white/10"
                      style={{
                        color: active
                          ? 'hsl(var(--color-accent))'
                          : 'hsl(var(--color-text-primary))',
                      }}
                    >
                      {Icon ? (
                        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      ) : (
                        <span className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center text-[10px] font-bold">
                          T
                        </span>
                      )}
                      <span>{item.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Separator */}
        <div
          className="h-px w-full flex-shrink-0"
          style={{ backgroundColor: 'hsl(var(--color-border))' }}
        />

        {/* Inline formatting buttons */}
        <div className="flex flex-col">
          {inlineFormats.map((format) => {
            const Icon = format.icon
            const active = format.isActive()
            return (
              <button
                key={format.id}
                onClick={format.action}
                className="p-1 transition-fast hover:bg-black/5 dark:hover:bg-white/10"
                style={{
                  color: active
                    ? 'hsl(var(--color-accent))'
                    : 'hsl(var(--color-text-secondary))',
                }}
                title={format.label}
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <BubbleMenu
      editor={editor}
      options={{
        placement: 'top',
        offset: 8,
        onHide: () => setShowBlockMenu(false),
      }}
      className="selection-toolbar"
    >
      <div
        className="flex items-center rounded-xl border shadow-xl overflow-visible"
        style={{
          backgroundColor: 'hsl(var(--color-bg-secondary))',
          borderColor: 'hsl(var(--color-border))',
        }}
      >
        {/* Block type dropdown */}
        <div className="relative">
          <button
            ref={dropdownTriggerRef}
            onClick={toggleBlockMenu}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-fast hover:bg-black/5 dark:hover:bg-white/10 rounded-l-xl border-r"
            style={{
              color: 'hsl(var(--color-text-secondary))',
              borderColor: 'hsl(var(--color-border))',
            }}
            title="Change block type"
          >
            <span>{getActiveBlockLabel()}</span>
            <ChevronDown className="w-3 h-3" />
          </button>

          {/* Block type dropdown menu */}
          {showBlockMenu && (
            <div
              className={`absolute left-0 w-48 rounded-xl border shadow-xl overflow-hidden z-50 ${
                dropdownDirection === 'down' ? 'top-full mt-1' : 'bottom-full mb-1'
              }`}
              style={{
                backgroundColor: 'hsl(var(--color-bg-secondary))',
                borderColor: 'hsl(var(--color-border))',
              }}
            >
              <div className="py-1">
                {blockItems.map((item) => {
                  const Icon = item.icon
                  const active = item.isActive()
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        item.action()
                        closeBlockMenu()
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-left transition-fast hover:bg-black/5 dark:hover:bg-white/10"
                      style={{
                        color: active
                          ? 'hsl(var(--color-accent))'
                          : 'hsl(var(--color-text-primary))',
                      }}
                    >
                      {Icon ? (
                        <Icon className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-xs font-bold">
                          T
                        </span>
                      )}
                      <span>{item.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Separator */}
        <div
          className="w-px h-5 flex-shrink-0"
          style={{ backgroundColor: 'hsl(var(--color-border))' }}
        />

        {/* Inline formatting buttons */}
        <div className="flex items-center px-0.5">
          {inlineFormats.map((format) => {
            const Icon = format.icon
            const active = format.isActive()
            return (
              <button
                key={format.id}
                onClick={format.action}
                className="p-1.5 rounded-lg transition-fast hover:bg-black/5 dark:hover:bg-white/10"
                style={{
                  color: active
                    ? 'hsl(var(--color-accent))'
                    : 'hsl(var(--color-text-secondary))',
                }}
                title={format.label}
              >
                <Icon className="w-4 h-4" />
              </button>
            )
          })}
        </div>
      </div>
    </BubbleMenu>
  )
}
